import {
  Component,
  ViewChild,
  OnInit,
  OnDestroy,
  effect,
  inject,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { PopoverModule, Popover } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { SelectModule } from 'primeng/select';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { PreferencesService, AuthService } from '../../core/services';
import { User } from '../../models/user';
import { BalanceStore } from '../../core/stores';
import { HeaderToggleService } from '../../core/services/header-toggle.service';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE, Language } from '../../core/constants';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  HelpCenterComponent,
  BalanceHistoryComponent,
} from '../../features/dialogs';
import { UserPopoverComponent } from '../../features/others/user-popover/user-popover.component';
import { environment } from '../../../environments/environment';
import { UserPermission } from '../../models/team';
import { PermissionDirective } from '../../shared/directives';

interface NavItem {
  iconClass: string;
  labelKey: string;
  route: string;
  active?: boolean;
  resourceName: string;
}


@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PopoverModule,
    ButtonModule,
    AvatarModule,
    SelectModule,
    DynamicDialogModule,
    TranslocoPipe,
    PermissionDirective,
    UserPopoverComponent,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  providers: [DialogService],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('userPopover') userPopover!: Popover;
  @ViewChild('teamsPopover') teamsPopover!: Popover;

  isDark = false;
  currentUser: User | null = null;
  balanceStore = inject(BalanceStore);
  preferencesService = inject(PreferencesService);
  authService = inject(AuthService);
  translocoService = inject(TranslocoService);
  dialogService = inject(DialogService);
  router = inject(Router);
  headerToggleService = inject(HeaderToggleService);

  private scrollSubscription?: Subscription;

  availableLanguages = AVAILABLE_LANGUAGES;
  currentLanguage: Language =
    AVAILABLE_LANGUAGES.find((lang) => lang.code === DEFAULT_LANGUAGE) || AVAILABLE_LANGUAGES[0];

  // App version info
  appVersion = environment.version;
  appName = environment.appName;

  // Key-value mapping for menu items by resource name
  menuItemsMap: { [key: string]: NavItem } = {
    'Chat': {
      iconClass: 'fa-regular fa-message',
      labelKey: 'header.nav.chat',
      active: true,
      route: '/ai-agent/chat',
      resourceName: 'Chat',
    },
    'Agents': {
      iconClass: 'fa-regular fa-robot',
      labelKey: 'header.nav.agents',
      active: false,
      route: '/ai-agent/agents',
      resourceName: 'Agents',
    },
    'Prompts': {
      iconClass: 'fa-regular fa-bolt',
      labelKey: 'header.nav.prompts',
      active: false,
      route: '/ai-agent/prompts',
      resourceName: 'Prompts',
    },
    'Models': {
      iconClass: 'fa-regular fa-microchip',
      labelKey: 'header.nav.models',
      active: false,
      route: '/ai-agent/ai-models',
      resourceName: 'Models',
    },
    'Knowledge Base': {
      iconClass: 'fa-regular fa-book-open',
      labelKey: 'header.nav.kb',
      active: false,
      route: '/ai-agent/kb',
      resourceName: 'Knowledge Base',
    },
    'Teams': {
      iconClass: 'pi pi-users',
      labelKey: 'header.nav.teams.label',
      active: false,
      route: '/ai-agent/teams',
      resourceName: 'Team',
    },
  };

  navItems: NavItem[] = [];
  permissions: UserPermission[] = [];
  activeMenuItem = '/';
  isMobileMenuOpen = false;
  isTeamsMenuOpen = false;

  constructor() {
    // Initialize all nav items from the map
    this.navItems = Object.values(this.menuItemsMap);

    effect(() => {
      this.currentUser = this.authService.currentUser?.user || null;
    });
  }

  get currentUserBalance(): string {
    return this.balanceStore.balance.formatted();
  }

  get balanceLoading(): boolean {
    return this.balanceStore.balance.loading();
  }

  async ngOnInit() {
    // Balance is loaded from app component, no need to load here
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateActiveNavItem();
      // Close mobile menu on navigation
      this.isMobileMenuOpen = false;
    });

    this.updateActiveNavItem();
    this.checkTheme();
    this.initializeLanguage();
  }

  ngOnDestroy() {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
  }

  updateActiveNavItem() {
    this.activeMenuItem = this.router.url;
  }

  private checkTheme() {
    this.isDark = this.preferencesService.darkMode;
    if (this.isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  navigateToRoute(route: string) {
    this.router.navigate([route]);
  }

  toggleTeamsPopover(event: any) {
    this.teamsPopover.toggle(event);
  }

  navigateToTeamsGridView() {
    this.teamsPopover.hide();
    this.isTeamsMenuOpen = false;
    this.router.navigate(['/ai-agent/teams']);
  }

  navigateToTeamsTreeView() {
    this.teamsPopover.hide();
    this.isTeamsMenuOpen = false;
    this.router.navigate(['/ai-agent/teams-tree']);
  }

  isTeamsRoute(): boolean {
    return this.activeMenuItem.includes('/ai-agent/teams');
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  toggleTheme() {
    this.preferencesService.toggleDarkMode();
    this.isDark = this.preferencesService.darkMode;
    if (this.isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  toggleUserPopover(event: any) {
    this.userPopover.toggle(event);
  }

  onLogoutClick() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  goToProfile() {
    this.userPopover.hide();
    this.router.navigate(['/profile']);
  }

  goToBalanceHistory() {
    this.dialogService.open(BalanceHistoryComponent, {
      header: this.translocoService.translate('header.user.balanceHistory'),
      width: '100vw',
      styleClass: 'balance-history-dialog',
      modal: true,
      maximizable: true,
      dismissableMask: true,
      closable: true,
    });
    this.userPopover.hide();
  }

  goToHelpCenter() {
    this.dialogService.open(HelpCenterComponent, {
      header: this.translocoService.translate('header.user.helpCenter'),
      width: '60vw',
      styleClass: 'help-center-dialog',
      modal: true,
      dismissableMask: true,
      closable: true,
    });
    this.userPopover.hide();
  }

  goToSettings() {
    this.userPopover.hide();
    this.router.navigate(['/settings']);
  }

  logout() {
    this.authService.logout();
    this.userPopover.hide();
    this.router.navigate(['/login']);
  }


  private initializeLanguage() {
    const savedLanguage = localStorage.getItem('selectedLanguage') || DEFAULT_LANGUAGE;
    const language = this.availableLanguages.find((lang) => lang.code === savedLanguage);
    if (language) {
      this.currentLanguage = language;
      this.translocoService.setActiveLang(language.code);
    }
  }

  onLanguageSelect(language: Language) {
    this.currentLanguage = language;
    this.translocoService.setActiveLang(language.code);
    localStorage.setItem('selectedLanguage', language.code);

    const direction =
      this.currentLanguage.code === 'fa' || this.currentLanguage.code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = direction;
    document.documentElement.lang = this.currentLanguage.code;
  }

  selectLanguage(language: Language) {
    this.userPopover.hide();
    this.currentLanguage = language;
    this.translocoService.setActiveLang(language.code);
    localStorage.setItem('selectedLanguage', language.code);

    const direction =
      this.currentLanguage.code === 'fa' || this.currentLanguage.code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = direction;
    document.documentElement.lang = this.currentLanguage.code;
  }
}
